<template>
  <section>
    <section class="preview-link">
      <div v-if="product" class="preview-title">
        <div>
          <p>{{ product.code }}</p>
          <div v-html="product.name" style="margin: 0 auto;" />
        </div>

        <div>
          <a
            :href="productPreviewUrl(product)"
            target="_blank"
            class="button is-primary"
          >
            預覽
          </a>
        </div>
      </div>
    </section>

    <b-tabs
      v-if="product && area"
      v-model="activeTab"
      type="is-toggle"
      :expanded="false"
      :animated="false"
      class="boarder-tabs"
      destroyOnHide
    >
      <b-tab-item :label="$t('attributes.detail_info')" :disabled="loading">
        <basic-form
          :product="product"
          :situation-types="situationTypes"
          @updated="onUpdated"
          @deleted="onDeleted"
        />
      </b-tab-item>

      <b-tab-item label="團別描述" :disabled="loading">
        <product-remark-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="複寫旅行團" :disabled="loading">
        <override-form :product="product" />
      </b-tab-item>

      <b-tab-item label="售價設定" :disabled="loading">
        <cost-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="售價進階(旅客類型)" :disabled="loading">
        <traveler-type-list :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="售價進階(方案)" :disabled="loading">
        <plan-list :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="團費說明" :disabled="loading">
        <cost-desc-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="圖檔設定" :disabled="loading">
        <pictures-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="航班交通" :disabled="loading">
        <transfer-list :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="交通說明" :disabled="loading">
        <traffic-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="行程特色" :disabled="loading">
        <feature-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="每日行程" :disabled="loading">
        <schedule-list
          :product="product"
          :area="product.image_path"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="餐食住宿" :disabled="loading">
        <food-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="優惠說明" :disabled="loading">
        <notes-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="分類" :disabled="loading">
        <catalogs-form
          :product="product"
          :catalogs="area_catalogs"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="內頁區塊" :disabled="loading">
        <block-list :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.country_flag')" :disabled="loading">
        <countries-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.city_weather')" :disabled="loading">
        <weathers-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.seo')" :disabled="loading">
        <seo-form :product="product" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="行程相簿" :disabled="loading">
        <blogs-form
          :product="product"
          :catalog-list="product.catalog_list"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="航程管理" :disabled="loading">
        <voyage-list :product="product" @updated="onUpdated"></voyage-list>
      </b-tab-item>

      <b-loading :active.sync="loading" :is-full-page="false"></b-loading>
    </b-tabs>
  </section>
</template>

<script>
import { PRODUCTS_API, AREA_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import BasicForm from './BasicForm'
import ProductRemarkForm from './ProductRemarkForm'
import BlockList from './BlockList'
import CostForm from './CostForm'
import CostDescForm from './CostDescForm'
import PicturesForm from './PicturesForm'
import FeatureForm from './FeatureForm'
import FoodForm from './FoodForm'
import TrafficForm from './TrafficForm'
import NotesForm from './NotesForm'
import CatalogsForm from './CatalogsForm'
import BlogsForm from './BlogsForm'
import SeoForm from './SeoForm'
import ScheduleList from './ScheduleList'
import TransferList from './TransferList'
import CountriesForm from './CountriesForm'
import WeathersForm from './WeathersForm'
import OverrideForm from './OverrideForm'
import PlanList from './PlanList'
import TravelerTypeList from './TravelerTypeList'
import VoyageList from '@/components/products/VoyageList'

export default {
  name: 'ProductEditForm',
  components: {
    VoyageList,
    BasicForm,
    ProductRemarkForm,
    BlockList,
    CostForm,
    PlanList,
    TravelerTypeList,
    CostDescForm,
    PicturesForm,
    FeatureForm,
    FoodForm,
    TrafficForm,
    NotesForm,
    CatalogsForm,
    BlogsForm,
    SeoForm,
    ScheduleList,
    TransferList,
    CountriesForm,
    WeathersForm,
    OverrideForm
  },

  props: {
    code: {
      required: true,
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 0,
      product: null,
      area: null,
      situationTypes: [],
      area_catalogs: []
    }
  },
  watch: {
    code(newCode) {
      this.loadProductAsync(newCode)
    }
  },
  mounted() {
    this.loadProductAsync(this.code)
  },
  methods: {
    async loadProductAsync(code) {
      try {
        this.$emit('update:loading', true)

        const res = await this.axios.get(`${PRODUCTS_API}/${code}`)
        this.product = res.data.data

        const areaCode = this.product.area_code
        const [area_res, event_res, catalog_res] = await Promise.all([
          this.axios.get(`${AREA_API}/${areaCode}`),
          this.axios.get(`${AREA_API}/${areaCode}/events`),
          this.axios.get(`${AREA_API}/${areaCode}/catalogs`)
        ])

        this.area = area_res.data.data
        this.situationTypes = event_res.data.data
        this.area_catalogs = catalog_res.data.data
      } catch (e) {
        this.product = null
        this.area = null
        this.situationTypes = []
        this.area_catalogs = []

        notifyService.fetch_error('product', e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    async loadAreaAsync(area_code) {
      try {
        const res = await this.axios.get(`${AREA_API}/${area_code}`)
        this.area = res.data.data
      } catch (e) {
        this.area = null

        notifyService.fetch_error('area', e)
      }
    },
    onUpdated(product) {
      this.product = product
      this.loadAreaAsync(product.area_code)

      this.$emit('updated', product)
    },
    onDeleted(code) {
      this.$emit('deleted', code)
    }
  }
}
</script>
